import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { hrApi } from '@/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import type { HrManagerTeamSummary } from '@/types/hr';
import { showApiError } from '@/utils/apiError';

export function HrMssPortalPage() {
  const [summary, setSummary] = useState<HrManagerTeamSummary | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setSummary(await hrApi.mssSummary());
    } catch (err) {
      showApiError(err, 'Failed to load manager dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div>
      <PageHeader
        title="Manager Self Service"
        description="Team overview, pending approvals, and quick actions"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'HR', to: '/hr' }, { label: 'MSS' }]}
        actions={
          <Button variant="secondary" onClick={() => load()}>
            Refresh
          </Button>
        }
      />
      {loading && <p className="text-sm text-slate-500">Loading…</p>}
      {summary && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="card p-4">
            <p className="text-sm text-slate-500">Direct reports</p>
            <p className="text-2xl font-semibold">{summary.directReports}</p>
          </div>
          <div className="card p-4">
            <p className="text-sm text-slate-500">Pending leave</p>
            <p className="text-2xl font-semibold">{summary.pendingLeaves}</p>
          </div>
          <div className="card p-4">
            <p className="text-sm text-slate-500">Pending attendance</p>
            <p className="text-2xl font-semibold">{summary.pendingAttendance}</p>
          </div>
          <div className="card p-4">
            <p className="text-sm text-slate-500">My approval queue</p>
            <p className="text-2xl font-semibold">{summary.pendingApprovals}</p>
          </div>
        </div>
      )}
      <div className="mt-6 flex flex-wrap gap-2">
        <Link to="/hr/approvals?mine=1" className="btn btn-secondary">
          My approvals
        </Link>
        <Link to="/hr/leaves" className="btn btn-secondary">
          Team leave
        </Link>
        <Link to="/hr/attendance" className="btn btn-secondary">
          Team attendance
        </Link>
      </div>
    </div>
  );
}
